<template>
  <div class="natural-language-container">
    <!-- Hero Section with Gradient Background -->
    <div class="hero-section">
      <div class="hero-background">
        <div class="gradient-orb orb-1"></div>
        <div class="gradient-orb orb-2"></div>
        <div class="gradient-orb orb-3"></div>
      </div>
      
      <div class="hero-content">
        <div class="ai-logo">
          <i class="fa-solid fa-wand-magic-sparkles"></i>
        </div>
        <h2 class="hero-title">{{ reviewMode ? 'Review Your Budget' : 'AI-Powered Budget Setup' }}</h2>
        <p class="hero-subtitle">{{ reviewMode ? 'Make any adjustments before finalizing' : 'Just describe your finances in plain English—we\'ll handle the rest' }}</p>
        
        <div class="feature-badges" v-if="!reviewMode">
          <div class="badge-item">
            <i class="fa-solid fa-bolt"></i>
            <span>Instant Processing</span>
          </div>
          <div class="badge-item">
            <i class="fa-solid fa-brain"></i>
            <span>Smart Extraction</span>
          </div>
          <div class="badge-item">
            <i class="fa-solid fa-check-circle"></i>
            <span>No Forms</span>
          </div>
        </div>
        
        <div class="feature-badges" v-else>
          <div class="badge-item">
            <i class="fa-solid fa-pencil"></i>
            <span>Editable</span>
          </div>
          <div class="badge-item">
            <i class="fa-solid fa-check-double"></i>
            <span>Verify Amounts</span>
          </div>
          <div class="badge-item">
            <i class="fa-solid fa-trash"></i>
            <span>Remove Items</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Input Section -->
    <div class="content-section" v-if="!reviewMode">

      <!-- Input Section -->
      <div class="input-section">
        <label for="budgetDescription" class="input-label">
          <i class="fa-solid fa-message-dots me-2"></i>
          Tell us about your monthly finances
        </label>
        <p class="input-helper-text">
          Share your average monthly income and expenses to help us create a conservative, healthy spending pace. 
          We'll use this to build a generalized picture of your finances and set up a sustainable daily budget.
        </p>
        <div class="input-wrapper">
          <textarea 
            id="budgetDescription" 
            class="magic-textarea" 
            v-model="budgetDescription"
            :disabled="isProcessing"
            placeholder="Example: I make $4000 a month from my job. My rent is $1200, utilities are around $150, groceries about $400, car payment is $350, and insurance is $120 per month."
            rows="6" 
            @input="clearError"
          ></textarea>
          <div class="textarea-glow"></div>
        </div>
        <div class="input-hint">
          <i class="fa-solid fa-lightbulb"></i>
          <span>Example: "I make $3,500 monthly. Rent is $900, utilities $150, groceries $400, car payment $350"</span>
        </div>
      </div>

      <!-- Error Display -->
      <div v-if="error" class="error-display">
        <div class="error-content">
          <i class="fa-solid fa-triangle-exclamation"></i>
          <span>{{ error }}</span>
        </div>
      </div>

      <!-- Processing State -->
      <div v-if="isProcessing" class="processing-display">
        <div class="processing-animation">
          <div class="pulse-ring"></div>
          <div class="pulse-ring delay-1"></div>
          <div class="pulse-ring delay-2"></div>
          <i class="fa-solid fa-sparkles"></i>
        </div>
        <h5 class="processing-title">Analyzing Your Finances</h5>
        <p class="processing-text">Our AI is extracting income and expenses...</p>
      </div>

      <!-- Action Button -->
      <button 
        @click="processBudgetDescription" 
        class="magic-button"
        :disabled="!budgetDescription.trim() || isProcessing"
        :class="{ 'processing': isProcessing }"
      >
        <span class="button-content">
          <i v-if="!isProcessing" class="fa-solid fa-wand-magic-sparkles me-2"></i>
          <i v-else class="fa-solid fa-sparkles me-2"></i>
          {{ isProcessing ? 'Processing Magic...' : 'Process' }}
        </span>
        <span class="button-shine"></span>
      </button>

      <!-- Examples Section -->
      <div class="examples-section">
        <h5 class="examples-header">
          <i class="fa-solid fa-stars me-2"></i>
          Try These Examples
        </h5>
        <div class="examples-grid">
          <div class="example-card" @click="useExample(example1)" :class="{ 'disabled': isProcessing }">
            <div class="example-icon">
              <i class="fa-solid fa-1"></i>
            </div>
            <p class="example-text">"{{ example1 }}"</p>
            <div class="example-overlay">
              <i class="fa-solid fa-hand-pointer"></i>
              <span>Click to use</span>
            </div>
          </div>
          <div class="example-card" @click="useExample(example2)" :class="{ 'disabled': isProcessing }">
            <div class="example-icon">
              <i class="fa-solid fa-2"></i>
            </div>
            <p class="example-text">"{{ example2 }}"</p>
            <div class="example-overlay">
              <i class="fa-solid fa-hand-pointer"></i>
              <span>Click to use</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Review Section -->
    <div class="content-section" v-if="reviewMode">
      <!-- Income Review -->
      <div class="review-section">
        <h4 class="review-section-title">
          <i class="fa-solid fa-money-bill-wave me-2"></i>
          Monthly Income
        </h4>
        <div class="income-edit-wrapper">
          <span class="currency-symbol">$</span>
          <input 
            type="number" 
            v-model.number="processedData.grossIncome" 
            class="income-amount-input"
            step="0.01"
            min="0"
            placeholder="0.00"
          />
        </div>
      </div>

      <!-- Expenses Review -->
      <div class="review-section">
        <h4 class="review-section-title">
          <i class="fa-solid fa-receipt me-2"></i>
          Monthly Expenses
        </h4>
        <div class="expenses-list">
          <div 
            v-for="(expense, index) in processedData.expenses" 
            :key="index"
            class="expense-item"
          >
            <div class="expense-info">
              <input 
                type="text" 
                v-model="expense.name" 
                class="expense-name-input"
                placeholder="Expense name"
              />
              <div class="expense-amount-wrapper">
                <span class="currency-symbol">$</span>
                <input 
                  type="number" 
                  v-model.number="expense.amount" 
                  class="expense-amount-input"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                />
              </div>
            </div>
            <button 
              @click="removeExpense(index)" 
              class="remove-expense-btn"
              title="Remove expense"
            >
              <i class="fa-solid fa-trash"></i>
            </button>
          </div>
        </div>
        <button @click="addExpense" class="add-expense-btn">
          <i class="fa-solid fa-plus me-2"></i>
          Add Expense
        </button>
        <div class="review-total">
          <span class="total-label">Total Expenses:</span>
          <span class="total-amount expense-value">${{ formatAmount(totalExpenses) }}</span>
        </div>
      </div>

      <!-- Net Budget Preview -->
      <div class="review-section">
        <h4 class="review-section-title">
          <i class="fa-solid fa-calculator me-2"></i>
          Budget Summary
        </h4>
        <div class="budget-summary">
          <div class="summary-row">
            <span class="summary-label">Monthly Income:</span>
            <span class="summary-value income-value">${{ formatAmount(processedData.grossIncome) }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Monthly Expenses:</span>
            <span class="summary-value expense-value">-${{ formatAmount(totalExpenses) }}</span>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-row net-row">
            <span class="summary-label">Available for Spending:</span>
            <span class="summary-value" :class="netBudget >= 0 ? 'income-value' : 'expense-value'">
              ${{ formatAmount(netBudget) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Proration Notice (shown only when setting up mid-month) -->
      <div v-if="true" class="proration-notice">
        <div class="notice-icon">
          <i class="fa-solid fa-calendar-days"></i>
        </div>
        <div class="notice-content">
          <h5 class="notice-title">Partial Month Setup</h5>
          <p class="notice-text">
            Since you're starting mid-month, your first month's budget will be 
            prorated to {{ prorationPercentage }}% ({{ remainingDaysText }}).
            Starting next month, you'll use the full monthly amounts.
          </p>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="review-actions">
        <button @click="goBack" class="secondary-button">
          <i class="fa-solid fa-arrow-left me-2"></i>
          Start Over
        </button>
        <button 
          @click="confirmBudget" 
          class="magic-button"
          :disabled="netBudget < 0"
        >
          <span class="button-content">
            <i class="fa-solid fa-check me-2"></i>
            Confirm & Continue
          </span>
          <span class="button-shine"></span>
        </button>
      </div>

      <!-- Negative Budget Warning -->
      <div v-if="netBudget < 0" class="negative-budget-warning">
        <i class="fa-solid fa-lightbulb me-2"></i>
        <span>Looks like expenses are higher than income. Try adjusting the numbers above to balance things out.</span>
      </div>
    </div>
  </div>
</template>

<script>
import { API_ENDPOINTS, makeAuthenticatedApiRequest } from '../utils/apiConfig.ts';
import { useAuth } from '../composables/useAuth';
import { useErrorModal } from '../composables/useErrorModal';

export default {
  name: 'NaturalLanguageBudget',
  emits: ['budget-processed'],
  setup() {
    const { getAccessToken } = useAuth();
    const { showApiError } = useErrorModal();
    return { getAccessToken, showApiError };
  },
  data() {
    return {
      budgetDescription: '',
      isProcessing: false,
      error: null,
      reviewMode: false,
      processedData: null,
      example1: 'I make $4000 a month from my job. My rent is $1200, utilities are around $150, groceries about $400, car payment is $350, and insurance is $120 per month.',
      example2: 'My monthly income is $3500 from salary plus $200 from freelancing. I pay $900 for rent, $80 for internet, $250 for food, $45 for phone, and $300 for student loans.'
    };
  },
  computed: {
    totalExpenses() {
      if (!this.processedData || !this.processedData.expenses) return 0;
      return this.processedData.expenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);
    },
    netBudget() {
      if (!this.processedData) return 0;
      return this.processedData.grossIncome - this.totalExpenses;
    },
    isSettingUpMidMonth() {
      const today = new Date();
      return today.getDate() !== 1;
    },
    prorationPercentage() {
      if (!this.isSettingUpMidMonth) return 100;
      
      const today = new Date();
      const startDay = today.getDate();
      const totalDays = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
      const remainingDays = totalDays - startDay + 1;
      
      return Math.round((remainingDays / totalDays) * 100);
    },
    remainingDaysText() {
      if (!this.isSettingUpMidMonth) return '';
      
      const today = new Date();
      const startDay = today.getDate();
      const totalDays = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
      const remainingDays = totalDays - startDay + 1;
      
      return `${remainingDays} of ${totalDays} days`;
    }
  },
  methods: {
    async processBudgetDescription() {
      if (!this.budgetDescription.trim()) {
        this.error = 'Please enter a description of your monthly finances.';
        return;
      }

      this.isProcessing = true;
      this.error = null;

      try {
        const accessToken = await this.getAccessToken();
        const apiResult = await makeAuthenticatedApiRequest(
          API_ENDPOINTS.BUDGET_PROCESSING, 
          { description: this.budgetDescription.trim() },
          accessToken
        );

        // Extract the actual response data from the API result
        const result = apiResult.data;

        // Validate the response structure
        if (!result || typeof result !== 'object') {
          throw new Error('Invalid response format from AI service');
        }

        if (!Array.isArray(result.income) || !Array.isArray(result.expenses)) {
          throw new Error('AI service returned incomplete data');
        }

        // Transform the AI response to match our component's expected format
        this.processedData = {
          grossIncome: this.calculateTotalIncome(result.income),
          expenses: this.transformExpenses(result.expenses),
          rawAiResponse: result
        };

        // Switch to review mode
        this.reviewMode = true;

      } catch (error) {
        console.error('Error processing budget description:', error);
        this.showApiError(error);
      } finally {
        this.isProcessing = false;
      }
    },

    confirmBudget() {
      // Capture the current date as budget start date in full ISO 8601 format with timestamp
      const budgetStartDate = new Date().toISOString(); // Full ISO-8601: YYYY-MM-DDTHH:mm:ss.sssZ
      
      // Emit the processed data with budget start date
      this.$emit('budget-processed', {
        ...this.processedData,
        budgetStartDate
      });
    },

    goBack() {
      this.reviewMode = false;
      this.processedData = null;
    },

    removeExpense(index) {
      this.processedData.expenses.splice(index, 1);
    },

    addExpense() {
      this.processedData.expenses.push({
        name: '',
        amount: 0
      });
    },

    calculateTotalIncome(incomeArray) {
      return incomeArray.reduce((total, income) => total + (income.amount || 0), 0);
    },

    transformExpenses(expensesArray) {
      return expensesArray.map(expense => ({
        name: expense.description || 'Unnamed Expense',
        amount: expense.amount || 0
      }));
    },

    formatAmount(amount) {
      return (amount || 0).toFixed(2);
    },

    useExample(exampleText) {
      if (!this.isProcessing) {
        this.budgetDescription = exampleText;
        this.clearError();
      }
    },

    clearError() {
      this.error = null;
    }
  }
};
</script>

<style scoped>
/* Prevent text selection */
.natural-language-container,
.natural-language-container * {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* Allow text selection only in textarea */
.magic-textarea {
  user-select: text !important;
  -webkit-user-select: text !important;
  -moz-user-select: text !important;
  -ms-user-select: text !important;
}

.natural-language-container {
  max-width: 900px;
  margin: 0 auto;
  animation: fadeInUp 0.6s ease-out;
}

/* Hero Section */
.hero-section {
  position: relative;
  text-align: center;
  margin-top: 2rem;
  margin-bottom: 3rem;
  padding: 3rem 2rem;
  overflow: hidden;
  border-radius: 20px;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 0;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.6;
}

.orb-1 {
  width: 350px;
  height: 350px;
  background: linear-gradient(135deg, #36A2EB, #4BC0C0);
  top: -150px;
  left: -100px;
  animation: float1 20s ease-in-out infinite;
}

.orb-2 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #FF6384, #FF9F40);
  top: -100px;
  right: -100px;
  animation: float2 18s ease-in-out infinite;
}

.orb-3 {
  width: 250px;
  height: 250px;
  background: linear-gradient(135deg, #FFCE56, #4BC0C0);
  bottom: -100px;
  left: 50%;
  animation: float3 22s ease-in-out infinite;
}

@keyframes float1 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -30px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.9); }
}

@keyframes float2 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(-25px, 25px) scale(1.05); }
  66% { transform: translate(20px, -15px) scale(0.95); }
}

@keyframes float3 {
  0%, 100% { transform: translateX(-50%) translate(0, 0) scale(1); }
  33% { transform: translateX(-50%) translate(15px, -20px) scale(1.08); }
  66% { transform: translateX(-50%) translate(-30px, 15px) scale(0.92); }
}

.hero-content {
  position: relative;
  z-index: 1;
}

.ai-logo {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #36A2EB, #4BC0C0);
  border-radius: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  box-shadow: 0 15px 50px rgba(54, 162, 235, 0.4);
  animation: logoFloat 3s ease-in-out infinite;
  position: relative;
}

.ai-logo::before {
  content: '';
  position: absolute;
  inset: -3px;
  background: linear-gradient(135deg, #36A2EB, #4BC0C0, #FF6384);
  border-radius: 25px;
  z-index: -1;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.ai-logo:hover::before {
  opacity: 1;
  animation: rotate 3s linear infinite;
}

@keyframes logoFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.ai-logo i {
  font-size: 2.5rem;
  color: white;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
}

.hero-title {
  font-size: 2.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #36A2EB, #4BC0C0, #FF6384);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.75rem;
  animation: fadeInUp 0.8s ease-out 0.2s both;
}

.hero-subtitle {
  font-size: 1.2rem;
  color: var(--light-text-muted);
  margin: 0 0 2rem 0;
  animation: fadeInUp 0.8s ease-out 0.4s both;
}

.feature-badges {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  animation: fadeInUp 0.8s ease-out 0.6s both;
}

.badge-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.25rem;
  background: linear-gradient(135deg, rgba(54, 162, 235, 0.1), rgba(75, 192, 192, 0.1));
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--light-text);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.badge-item::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(54, 162, 235, 0.15), rgba(75, 192, 192, 0.15));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.badge-item:hover::before {
  opacity: 1;
}

.badge-item i {
  font-size: 1rem;
  color: #36A2EB;
  position: relative;
  z-index: 1;
}

.badge-item span {
  position: relative;
  z-index: 1;
}

/* Content Section */
.content-section {
  background: var(--light-card);
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  position: relative;
}

.content-section::before {
  content: '';
  position: absolute;
  inset: -2px;
  background: linear-gradient(135deg, #36A2EB, #4BC0C0, #FF6384, #FFCE56);
  border-radius: 20px;
  z-index: -1;
  opacity: 0.1;
  filter: blur(20px);
}

/* Input Section */
.input-section {
  margin-bottom: 2rem;
}

.input-label {
  display: block;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--light-text);
  margin-bottom: 1rem;
  text-align: center;
}

.input-wrapper {
  position: relative;
  margin-bottom: 1rem;
}

.magic-textarea {
  width: 100%;
  min-height: 160px;
  padding: 1.25rem;
  border: 2px solid var(--light-border);
  border-radius: 15px;
  background: var(--light-card);
  color: var(--light-text);
  font-size: 1.05rem;
  line-height: 1.6;
  resize: vertical;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

.magic-textarea:focus {
  outline: none;
  border-color: #36A2EB;
  background: var(--light-input-bg);
}

.magic-textarea:focus + .textarea-glow {
  opacity: 1;
}

.magic-textarea::placeholder {
  color: var(--light-text-muted);
  opacity: 0.6;
}

.magic-textarea:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.textarea-glow {
  position: absolute;
  inset: -2px;
  background: linear-gradient(135deg, #36A2EB, #4BC0C0);
  border-radius: 15px;
  z-index: 0;
  opacity: 0;
  transition: opacity 0.3s ease;
  filter: blur(10px);
}

.input-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: var(--light-text-muted);
}

.input-hint i {
  color: #FFCE56;
  font-size: 1rem;
}

/* Error Display */
.error-display {
  margin-bottom: 1.5rem;
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-10px); }
  75% { transform: translateX(10px); }
}

.error-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: rgba(220, 53, 69, 0.1);
  border: 2px solid var(--expense-color);
  border-radius: 12px;
  color: var(--expense-color);
  font-weight: 600;
}

.error-content i {
  font-size: 1.2rem;
}

/* Processing Display */
.processing-display {
  text-align: center;
  margin-bottom: 2rem;
  padding: 2rem;
  background: var(--nested-container-bg);
  border-radius: 15px;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.processing-animation {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
}

.pulse-ring {
  position: absolute;
  inset: 0;
  border: 3px solid #36A2EB;
  border-radius: 50%;
  animation: pulse 2s ease-out infinite;
}

.pulse-ring.delay-1 {
  animation-delay: 0.5s;
}

.pulse-ring.delay-2 {
  animation-delay: 1s;
}

@keyframes pulse {
  0% {
    transform: scale(0.5);
    opacity: 1;
  }
  100% {
    transform: scale(1.2);
    opacity: 0;
  }
}

.processing-animation i {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 2rem;
  color: #36A2EB;
  animation: sparkle 1.5s ease-in-out infinite;
}

@keyframes sparkle {
  0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
  50% { transform: translate(-50%, -50%) scale(1.2); opacity: 0.8; }
}

.processing-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--light-text);
  margin: 0 0 0.5rem 0;
}

.processing-text {
  font-size: 0.95rem;
  color: var(--light-text-muted);
  margin: 0;
}

/* Magic Button */
.magic-button {
  width: 100%;
  min-height: 60px;
  padding: 1rem 2rem;
  font-size: 1.15rem;
  font-weight: 700;
  border: none;
  border-radius: 50px;
  background: linear-gradient(135deg, #36A2EB, #4BC0C0);
  color: white;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 8px 30px rgba(54, 162, 235, 0.4);
  margin-bottom: 2rem;
}

.magic-button:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 12px 40px rgba(54, 162, 235, 0.5);
}

.magic-button:active:not(:disabled) {
  transform: translateY(-1px);
}

.magic-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.magic-button.processing {
  animation: processingPulse 2s ease-in-out infinite;
}

@keyframes processingPulse {
  0%, 100% { box-shadow: 0 8px 30px rgba(54, 162, 235, 0.4); }
  50% { box-shadow: 0 8px 40px rgba(54, 162, 235, 0.6); }
}

.button-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.button-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s ease;
}

.magic-button:hover:not(:disabled) .button-shine {
  left: 100%;
}

/* Examples Section */
.examples-section {
  padding: 2rem;
  background: var(--nested-container-bg);
  border-radius: 15px;
}

.examples-header {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--light-text);
  margin: 0 0 1.5rem 0;
  text-align: center;
}

.examples-grid {
  display: grid;
  gap: 1rem;
}

.example-card {
  position: relative;
  padding: 1.5rem;
  background: var(--light-card);
  border: 2px solid var(--light-border);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
}

.example-card:hover:not(.disabled) {
  border-color: #36A2EB;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(54, 162, 235, 0.2);
}

.example-card.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.example-icon {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #36A2EB, #4BC0C0);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.9rem;
  font-weight: 700;
}

.example-text {
  font-size: 0.95rem;
  color: var(--light-text);
  line-height: 1.5;
  margin: 0;
  padding-right: 3rem;
  font-style: italic;
}

.example-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(54, 162, 235, 0.9), rgba(75, 192, 192, 0.9));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.3s ease;
  color: white;
  font-weight: 700;
}

.example-card:hover:not(.disabled) .example-overlay {
  opacity: 1;
}

.example-overlay i {
  font-size: 1.5rem;
}

/* Dark Mode Support */
:global([data-theme="dark"]) .ai-logo {
  box-shadow: 0 15px 50px rgba(54, 162, 235, 0.6);
}

:global([data-theme="dark"]) .badge-item {
  background: linear-gradient(135deg, rgba(54, 162, 235, 0.15), rgba(75, 192, 192, 0.15));
}

:global([data-theme="dark"]) .content-section {
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

:global([data-theme="dark"]) .magic-button {
  box-shadow: 0 8px 30px rgba(54, 162, 235, 0.5);
}

:global([data-theme="dark"]) .magic-button:hover:not(:disabled) {
  box-shadow: 0 12px 40px rgba(54, 162, 235, 0.6);
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-section {
    padding: 2rem 1rem;
    margin-bottom: 2rem;
    margin-top: 0;
  }

  .gradient-orb {
    filter: blur(60px);
  }

  .orb-1 {
    width: 250px;
    height: 250px;
  }

  .orb-2 {
    width: 200px;
    height: 200px;
  }

  .orb-3 {
    width: 180px;
    height: 180px;
  }

  .ai-logo {
    width: 80px;
    height: 80px;
    border-radius: 20px;
  }

  .ai-logo i {
    font-size: 2rem;
  }

  .hero-title {
    font-size: 1.8rem;
  }

  .hero-subtitle {
    font-size: 1rem;
  }

  .feature-badges {
    gap: 0.75rem;
  }

  .badge-item {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
  }

  .content-section {
    padding: 1.5rem;
  }

  .input-label {
    font-size: 1rem;
  }

  .magic-textarea {
    min-height: 140px;
    font-size: 1rem;
  }

  .magic-button {
    font-size: 1.05rem;
    min-height: 54px;
  }

  .examples-section {
    padding: 1.5rem;
  }

  .example-card {
    padding: 1.25rem;
  }

  .example-text {
    font-size: 0.9rem;
    padding-right: 2.5rem;
  }

  .example-icon {
    width: 28px;
    height: 28px;
    font-size: 0.8rem;
  }
}

/* Animation */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Review Section Styles */
.review-section {
  background: var(--nested-container-bg);
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 2px dashed var(--light-border);
  transition: all 0.2s ease;
}

.review-section-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--light-text);
  margin: 0 0 1.25rem 0;
  display: flex;
  align-items: center;
}

.review-section-title i {
  color: var(--light-text);
}

.review-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: var(--light-card);
  border-radius: 10px;
  margin-top: 1rem;
}

.total-label {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--light-text);
}

.total-amount {
  font-size: 1.5rem;
  font-weight: 700;
}

.income-value {
  color: var(--income-color);
}

.expense-value {
  color: var(--expense-color);
}

/* Income Edit */
.income-edit-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  background: var(--light-card);
  border: 2px solid var(--light-border);
  border-radius: 10px;
  transition: all 0.2s ease;
}

.income-edit-wrapper:focus-within {
  border-color: #36A2EB;
}

.income-edit-wrapper .currency-symbol {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--light-text-muted);
}

.income-amount-input {
  flex: 1;
  border: none;
  background: transparent;
  color: var(--income-color);
  font-size: 1.8rem;
  font-weight: 700;
  min-width: 0;
}

.income-amount-input:focus {
  outline: none;
}

/* Expenses List */
.expenses-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.expense-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--light-card);
  border-radius: 10px;
  transition: all 0.2s ease;
}

.expense-item:hover {
  border-color: #36A2EB;
}

.expense-info {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.expense-name-input {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 2px solid var(--light-border);
  border-radius: 8px;
  background: var(--light-card);
  color: var(--light-text);
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.expense-name-input:focus {
  outline: none;
  border-color: #36A2EB;
  background: var(--light-input-bg);
}

.expense-amount-wrapper {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  background: var(--nested-container-bg);
  border: 2px solid var(--light-border);
  border-radius: 8px;
  min-width: 140px;
  transition: all 0.2s ease;
}

.expense-amount-wrapper:focus-within {
  border-color: #36A2EB;
}

.currency-symbol {
  font-size: 1rem;
  font-weight: 600;
  color: var(--light-text-muted);
  background: none !important;
  border: none !important;
}

.expense-amount-input {
  flex: 1;
  border: none;
  background: transparent;
  color: var(--light-text);
  font-size: 1rem;
  font-weight: 700;
  text-align: right;
  min-width: 0;
}

.expense-amount-input:focus {
  outline: none;
}

.remove-expense-btn {
  width: 40px;
  height: 40px;
  border: 2px solid var(--light-border);
  border-radius: 10px;
  background: var(--light-card);
  color: var(--light-border);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.remove-expense-btn:hover {
  background: var(--expense-color);
  border-color: var(--expense-color);
  color: white;
  transform: scale(1.05);
}

.remove-expense-btn i {
  font-size: 0.9rem;
}

/* Add Expense Button */
.add-expense-btn {
  width: 100%;
  padding: 0.875rem 1.5rem;
  border: 2px dashed var(--light-border);
  border-radius: 10px;
  background: var(--nested-container-bg);
  color: var(--light-text);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.75rem;
}

.add-expense-btn:hover {
  border-color: #36A2EB;
  color: #36A2EB;
  background: var(--light-card);
  transform: translateY(-1px);
}

.add-expense-btn:active {
  transform: translateY(0);
}

.add-expense-btn i {
  font-size: 0.9rem;
}

/* Budget Summary */
.budget-summary {
  background: var(--nested-container-bg);
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: var(--light-card);
  border-radius: 8px;
}

.summary-label {
  font-size: 1rem;
  font-weight: 600;
  color: var(--light-text);
}

.summary-value {
  font-size: 1.1rem;
  font-weight: 700;
}

.summary-divider {
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--light-border), transparent);
  margin: 0.5rem 0;
}

.net-row {
  background: linear-gradient(135deg, rgba(54, 162, 235, 0.15), rgba(75, 192, 192, 0.15));
}

.net-row .summary-label {
  font-size: 1.1rem;
}

.net-row .summary-value {
  font-size: 1.5rem;
}

/* Review Actions */
.review-actions {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
}

.secondary-button {
  flex: 1;
  min-height: 60px;
  padding: 1rem 2rem;
  font-size: 1.05rem;
  font-weight: 600;
  border: 2px solid var(--light-border);
  border-radius: 50px;
  background: var(--light-card);
  color: var(--light-text);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.secondary-button:hover {
  border-color: #36A2EB;
  color: #36A2EB;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(54, 162, 235, 0.2);
}

.secondary-button:active {
  transform: translateY(0);
}

.review-actions .magic-button {
  flex: 2;
  margin-bottom: 0;
}

/* Responsive - Review Section */
@media (max-width: 768px) {
  .review-section {
    padding: 1.25rem;
  }

  .review-section-title {
    font-size: 1.1rem;
  }

  .review-total {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }

  .total-amount {
    font-size: 1.3rem;
  }

  .income-edit-wrapper {
    justify-content: center;
  }

  .income-amount-input {
    font-size: 1.5rem;
    text-align: center;
  }

  .expense-info {
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
  }

  .expense-name-input {
    width: 100%;
  }

  .expense-amount-wrapper {
    width: 100%;
    justify-content: center;
  }

  .expense-item {
    flex-direction: column;
    align-items: stretch;
  }

  .remove-expense-btn {
    width: 100%;
    height: 44px;
  }

  .summary-row {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }

  .net-row .summary-value {
    font-size: 1.3rem;
  }

  .review-actions {
    flex-direction: column;
  }

  .secondary-button,
  .review-actions .magic-button {
    flex: 1;
    width: 100%;
  }
}

/* Negative Budget Warning */
.negative-budget-warning {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  background: rgba(255, 206, 86, 0.1);
  border: 2px solid rgba(255, 206, 86, 0.5);
  border-radius: 12px;
  color: var(--light-text);
  font-weight: 500;
  margin-top: 2rem;
  animation: fadeIn 0.3s ease-out;
}

.negative-budget-warning i {
  font-size: 1.2rem;
  color: #FFCE56;
}

/* Proration Notice */
.proration-notice {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem;
  background: var(--nested-container-bg);
  border: 2px dashed var(--light-border);
  border-radius: 15px;
  margin-bottom: 1.5rem;
  transition: all 0.2s ease;
  animation: fadeIn 0.3s ease-out;
}

.notice-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #36A2EB, #4BC0C0);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.notice-icon i {
  font-size: 1.5rem;
  color: white;
}

.notice-content {
  flex: 1;
}

.notice-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--light-text);
  margin: 0 0 0.5rem 0;
}

.notice-text {
  font-size: 0.95rem;
  color: var(--light-text-muted);
  line-height: 1.5;
  margin: 0;
}

@media (max-width: 768px) {
  .proration-notice {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 1rem;
  }

  .notice-icon {
    width: 44px;
    height: 44px;
  }

  .notice-icon i {
    font-size: 1.3rem;
  }

  .notice-title {
    font-size: 1rem;
  }

  .notice-text {
    font-size: 0.9rem;
  }
}
</style>